import React, { useMemo, useState } from 'react';
import { AgCharts } from 'ag-charts-react';
import { AgChartOptions } from 'ag-charts-community';
import { TSpeedDraughtValueItem, TVesselSpeedDraughtValues } from '../../../kpiTypes';
import { ChartContainer } from '../../../../../shared/components/charts/ChartContainer';

export interface ILoadedConditionSpeedChartProps {
	vesselSpeedAndDraughtValues: TVesselSpeedDraughtValues;
}

const LoadedConditionSpeedChart: React.FC<ILoadedConditionSpeedChartProps> = ({ ...props }) => {
	const parsedData = useMemo(() => {
		const dataList = props.vesselSpeedAndDraughtValues?.dataList || [];
		return Array.isArray(dataList)
			? dataList.map((item: TSpeedDraughtValueItem) => {
					return {
						...item,
						signalDateTime: new Date(item.signalDateTime), // Ensure each signalDate is a Date object
					};
				})
			: [];
	}, [props.vesselSpeedAndDraughtValues?.dataList]);

	const [options] = useState<AgChartOptions>({
		series: [
			{
				type: 'scatter',
				title: 'Ballast',
				data: Array.isArray(parsedData) ? parsedData.filter((v) => v.loadedCondition === 'Ballast') : [],
				xKey: 'signalDateTime',
				xName: 'signalDateTime',
				yKey: 'speed',
				yName: 'speed',
				fill: '#f0d035',
				stroke: '#f0d035',
				shape: 'circle',
				size: 1,
				tooltip: {
					renderer: function ({ datum, xKey, yKey }) {
						const date = new Date(datum[xKey]);
						const formattedDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:00`;
						return { data: undefined, title: 'Ballast', heading: `${formattedDate}: ${datum[yKey].toFixed(2)}` };
					},
				},
			},
			{
				type: 'scatter',
				title: 'Laden',
				data: Array.isArray(parsedData) ? parsedData.filter((v) => v.loadedCondition === 'Laden') : [],
				xKey: 'signalDateTime',
				xName: 'signalDateTime',
				yKey: 'speed',
				yName: 'speed',
				fill: '#183d57',
				stroke: '#183d57',
				shape: 'circle',
				size: 1,
				tooltip: {
					renderer: function ({ datum, xKey, yKey }) {
						const date = new Date(datum[xKey]);
						const formattedDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:00`;
						return { data: undefined, title: 'Laden', heading: `${formattedDate}: ${datum[yKey].toFixed(2)}` };
					},
				},
			},
		],
		axes: [
			{
				type: 'time',
				position: 'bottom',
				label: {
					autoRotate: false,
					formatter: (params) => {
						const date = new Date(params.value);
						const month = date.getMonth();
						if (month % 2 === 0) {
							return date.toLocaleString('default', { month: 'short', year: 'numeric' });
						}
						return '';
					},
				},
			},
			{
				type: 'number',
				position: 'left',
				title: {
					text: 'Speed',
				},
			},
		],

		legend: {
			position: 'top',
		},
	});

	return (
		<ChartContainer
			data={parsedData}
			filename="loaded_condition_speed_chart"
			buttonPosition="top-right"
		>
			<AgCharts key={props.vesselSpeedAndDraughtValues.imoNumber} options={options} className="w-full h-full" />
		</ChartContainer>
	);
};

export default LoadedConditionSpeedChart;
